import React, { useState, useEffect } from "react"
import { Loader2 } from "lucide-react"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "src/components/ui/sheet"
import { Input } from "src/components/ui/input"
import { Textarea } from "src/components/ui/textarea"
import { Button } from "src/components/ui/button"
import { FormItem, FormLabel, FormControl } from "src/components/ui/form"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select"
import { useToast } from "src/components/ui/toast"
import { createFollowUp, updateFollowUp } from "../api/followUpsApi"
import { FOLLOW_UP_CHANNELS } from "../constants"

const PRIORITIES = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
]

function toLocalDatetimeValue(date) {
  if (!date) return ""
  const d = new Date(date)
  const pad = (n) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// Default a new follow-up to tomorrow 10:00 — a sensible "reach out again" slot.
function defaultDueAt() {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  d.setHours(10, 0, 0, 0)
  return toLocalDatetimeValue(d)
}

const EMPTY = { title: "", channel: "call", description: "", priority: "medium", due_at: "", assignee_user_id: "" }

export function FollowUpFormDrawer({ open, onClose, onSaved, followUp = null, parent, members = [] }) {
  const { toast } = useToast()
  const [saving, setSaving] = useState(false)
  const isEdit = !!followUp
  const [form, setForm] = useState(EMPTY)

  useEffect(() => {
    if (!open) return
    if (followUp) {
      setForm({
        title: followUp.title || "",
        channel: followUp.channel || "call",
        description: followUp.description || "",
        priority: followUp.priority || "medium",
        due_at: toLocalDatetimeValue(followUp.due_at),
        assignee_user_id: followUp.assignee?.id ? String(followUp.assignee.id) : "",
      })
    } else {
      setForm({ ...EMPTY, due_at: defaultDueAt() })
    }
  }, [open, followUp])

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.due_at) {
      toast({ variant: "destructive", title: "Pick a date for the follow-up" })
      return
    }
    setSaving(true)
    try {
      const payload = {
        title: form.title.trim() || null,
        channel: form.channel,
        description: form.description.trim() || null,
        priority: form.priority,
        due_at: form.due_at || null,
        assignee_user_id: form.assignee_user_id || null,
      }
      const saved = isEdit
        ? await updateFollowUp(followUp.id, payload)
        : await createFollowUp(parent.type, parent.id, payload)
      toast({ title: isEdit ? "Follow-up updated" : "Follow-up scheduled" })
      onSaved?.(saved)
      onClose()
    } catch (err) {
      toast({ variant: "destructive", title: "Could not save follow-up", description: err.response?.data?.errors?.[0] })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="flex flex-col p-0 sm:max-w-[480px]">
        <SheetHeader>
          <SheetTitle>{isEdit ? "Edit follow-up" : "Schedule follow-up"}</SheetTitle>
          <SheetDescription>
            {isEdit ? "Update the follow-up details below." : "Plan when and how you'll reach out next."}
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
            {/* Channel */}
            <FormItem>
              <FormLabel>Channel</FormLabel>
              <div className="grid grid-cols-3 gap-2">
                {FOLLOW_UP_CHANNELS.map(({ value, label, Icon, cls }) => {
                  const active = form.channel === value
                  return (
                    <Button
                      key={value}
                      type="button"
                      variant="outline"
                      onClick={() => set("channel", value)}
                      className={`flex h-auto flex-col items-center gap-1 rounded-lg border px-2 py-2.5 text-[12px] font-normal transition-colors ${
                        active
                          ? "border-slate-900 bg-slate-900/[0.03] font-medium text-slate-900"
                          : "border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700"
                      }`}
                    >
                      <span className={`flex h-6 w-6 items-center justify-center rounded-full ${cls}`}>
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      {label}
                    </Button>
                  )
                })}
              </div>
            </FormItem>

            {/* Due date */}
            <FormItem>
              <FormLabel>
                When <span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  type="datetime-local"
                  value={form.due_at}
                  onChange={(e) => set("due_at", e.target.value)}
                />
              </FormControl>
            </FormItem>

            {/* Title (optional — defaults from channel + record) */}
            <FormItem>
              <FormLabel>Title</FormLabel>
              <FormControl>
                <Input
                  placeholder="Auto-filled from the channel if left blank"
                  value={form.title}
                  onChange={(e) => set("title", e.target.value)}
                />
              </FormControl>
            </FormItem>

            {/* Notes */}
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Textarea
                  rows={3}
                  placeholder="What's this follow-up about?"
                  value={form.description}
                  onChange={(e) => set("description", e.target.value)}
                />
              </FormControl>
            </FormItem>

            {/* Priority */}
            <FormItem>
              <FormLabel>Priority</FormLabel>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormItem>

            {/* Assignee */}
            {members.length > 0 && (
              <FormItem>
                <FormLabel>Assign to</FormLabel>
                <Select
                  value={form.assignee_user_id || "unassigned"}
                  onValueChange={(v) => set("assignee_user_id", v === "unassigned" ? "" : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select team member" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {members.map((m) => (
                      <SelectItem key={m.id} value={String(m.id)}>
                        {m.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-6 py-4">
            <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !form.due_at} className="gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEdit ? "Save changes" : "Schedule"}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
